import {
  IOrganizationSetQueryVariables,
  ISocialPageGroupSetQuery,
  ISocialPageGroupSetQueryVariables,
  useInfiniteOrganizationSetQuery,
  useInfiniteSocialPageGroupSetQuery,
} from "@smp/graphql";
import { GqlError } from "@smp/graphql/types";
import { useMemo } from "react";
import { UseInfiniteQueryOptions } from "react-query";

import { gqlClient } from "@/hooks/use_graphql";

type PageGroup = ISocialPageGroupSetQuery["social_page_group_set"][number];

export const useQueryOrganizationSet = (
  options?: Omit<
    UseInfiniteQueryOptions<ISocialPageGroupSetQuery, GqlError>,
    "queryFn" | "queryKey" | "getNextPageParam"
  >,
) => {
  const queryOrganizationSet = useInfiniteOrganizationSetQuery(
    "id__in",
    gqlClient,
    undefined,
    {
      onError: options?.onError,
    },
  );

  const organizationSet = useMemo(
    () =>
      queryOrganizationSet.data?.pages.flatMap((page) => page.organization_set),
    [queryOrganizationSet.data?.pages],
  );

  const querySocialPageGroupSet = useInfiniteSocialPageGroupSetQuery(
    "all_organization_contexts",
    gqlClient,
    {
      all_organization_contexts: true,
      all_participated_organization_contexts: true,
    },
    {
      ...options,
      enabled: !!organizationSet?.length && options?.enabled !== false,
    },
  );

  const data = useMemo(() => {
    const map = querySocialPageGroupSet.data?.pages.reduce<
      Map<number, Array<Omit<PageGroup, "organization">>>
    >((acc, { social_page_group_set }) => {
      social_page_group_set.forEach((group) => {
        const { organization, ...spread } = group;
        const organizationId = group.is_locked_for_update
          ? group.organization_after_update?.id
          : group.organization?.id;

        if (!organizationId) {
          return;
        }

        const socialPageGroupSet = acc.get(organizationId) || [];

        acc.set(organizationId, [...socialPageGroupSet, spread]);
      });

      return acc;
    }, new Map());

    return (
      organizationSet?.map((organization) => ({
        ...organization,
        social_page_group_set: map?.get(organization.id) || [],
      })) || []
    );
  }, [organizationSet, querySocialPageGroupSet.data?.pages]);

  return {
    ...querySocialPageGroupSet,
    isLoading:
      queryOrganizationSet.isLoading || querySocialPageGroupSet.isLoading,
    isFetching:
      queryOrganizationSet.isFetching || querySocialPageGroupSet.isFetching,
    data,
  };
};

useQueryOrganizationSet.getKeys = (
  socialPageGroupsVars?: ISocialPageGroupSetQueryVariables,
  organizationVars?: IOrganizationSetQueryVariables,
) => ({
  socialPageGroupSet:
    useInfiniteSocialPageGroupSetQuery.getKey(socialPageGroupsVars),
  organizationSet: useInfiniteOrganizationSetQuery.getKey(organizationVars),
});
